import { z } from "zod";
//#region src/accelerator-table.ts
/**
* The accelerator table holds one row per GPU model that a pool tier may name. Each row has one
* entry per cloud that sells the model (2026-10-24-pool-machine-generations, D-10, D-44).
*
* Why it exists. A GPU tier names a model (`h100`) and a GPU count, never a machine family. No
* key names a model the same way on both clouds: GKE labels a node with the GCP accelerator type
* (`nvidia-h100-80gb`), and the GCP family `a3` holds H100, H100 Mega and H200 shapes. So the
* platform owns this table, and every reader of a model name reads it here.
*
* Who reads it:
*
* - `checkAppPools` in `app-pool-rules.ts`, at both publish doors. A tier whose model has no entry
*   for the tier's cloud, or whose GPU count the cloud does not sell for that model, is refused.
* - The operator (S2, S4), which keeps the catalog entries whose `gpu.acceleratorType` is in the
*   set {@link acceleratorTypesFor} gives for the tier's model, cloud and GPU count. That set pairs
*   each accelerator type with the counts it sells, so a one-GPU `h100` tier never matches the
*   eight-GPU Mega type.
*
* What the table holds. The GCP entries (stage S1) cover every GPU family of the GCP ladders
* (`g2`, `g4`, `a3`, `a4`, `a4x`). GCP sells no L40S, so `l40s` has no GCP entry. The AWS entries
* (stage S4, WS-4a) cover every GPU family of the AWS ladders (`g6`, `g6e`, `p5`, `p5e`, `p5en`,
* `p6-b200`) and five models: `l4`, `l40s`, `h100`, `h200` and `b200`. `gb200`, `gb300` and
* `rtx-pro-6000` have no AWS entry: `p6e-gb200` runs an arm64 Grace CPU and every AWS node group
* runs an x86_64 AMI, and the research read no AWS shape of the other two. A model with no entry
* for a cloud has no shape on that cloud, and a tier that names it there never places.
*
* The accelerator type names. An AWS entry carries the GCP name of the same NVIDIA part
* (`nvidia-h100-80gb` on both clouds). AWS has no accelerator type name of its own, and a fixture
* pin (`MachineTypeAsk.accelerator`) is one name that must mean one part on either cloud.
*
* Sources: `research/gpu-accelerators.md` section 3 (Google's GPU machine type pages, read
* 2026-10-24) and section 4 (the AWS accelerated instance type tables). Every accelerator type
* matches the label-value grammar the node group schema enforces
* (`^[a-z0-9]([a-z0-9-]*[a-z0-9])?$`, at most 63 characters).
*
* Pure data and two pure readers. No I/O.
*/
/**
* The GPU models a pool tier may name (D-10), in the order of their first market release. The
* order is visible only in messages that list the models; nothing resolves by position.
*
* - `l4`: NVIDIA L4, inference.
* - `l40s`: NVIDIA L40S, inference.
* - `h100`: NVIDIA H100 80 GB, both the standard and the Mega form.
* - `h200`: NVIDIA H200 141 GB.
* - `b200`: NVIDIA B200.
* - `gb200`: NVIDIA GB200 (Grace Blackwell).
* - `rtx-pro-6000`: NVIDIA RTX PRO 6000 Blackwell Server Edition, inference.
* - `gb300`: NVIDIA GB300 (Grace Blackwell Ultra).
*
* Frozen: the publish rules and {@link isAcceleratorModel} read it, so a consumer that mutates it
* gets a `TypeError`.
*/
const ACCELERATOR_MODELS = Object.freeze([
	"l4",
	"l40s",
	"h100",
	"h200",
	"b200",
	"gb200",
	"rtx-pro-6000",
	"gb300"
]);
/** The zod enum over {@link ACCELERATOR_MODELS}. Each member parses to itself; any other value,
*  a different case included (`H100`), is a `ZodError`. */
const acceleratorModelSchema = z.enum(ACCELERATOR_MODELS);
/** True when `value` is a member of {@link ACCELERATOR_MODELS}. Total over any input: a
*  non-string, an empty string and `'constructor'` all answer `false`. */
function isAcceleratorModel(value) {
	return typeof value === "string" && ACCELERATOR_MODELS.includes(value);
}
/** One frozen entry. Each shape is `[acceleratorType, gpuCounts]`; the entry derives
*  `acceleratorTypes` and the ascending union `gpuCounts` from the shapes. */
function entry(shapes, families) {
	const counts = [...new Set(shapes.flatMap(([, c]) => c))].sort((a, b) => a - b);
	return Object.freeze({
		shapes: Object.freeze(shapes.map(([acceleratorType, gpuCounts]) => Object.freeze({
			acceleratorType,
			gpuCounts: Object.freeze([...gpuCounts])
		}))),
		acceleratorTypes: Object.freeze(shapes.map(([t]) => t)),
		families: Object.freeze([...families]),
		gpuCounts: Object.freeze(counts)
	});
}
/**
* The table: for each model, the entry of each cloud that sells it. A cloud with no key for a
* model sells no shape of it. Frozen at every level.
*
* GCP entries (Google's GPU machine type pages):
*
* - `l4`: `nvidia-l4` on `g2`; g2 shapes carry 1, 2, 4 or 8 GPUs.
* - `l40s`: no GCP entry.
* - `h100`: `nvidia-h100-80gb` (a3-highgpu, a3-edgegpu) and `nvidia-h100-mega-80gb` (a3-megagpu)
*   on `a3`. `nvidia-h100-80gb` sells 1, 2, 4 or 8 GPUs (a3-highgpu); `nvidia-h100-mega-80gb`
*   sells 8 only.
* - `h200`: `nvidia-h200-141gb` on `a3` (a3-ultragpu), 8 GPUs.
* - `b200`: `nvidia-b200` on `a4` (a4-highgpu), 8 GPUs.
* - `gb200`: `nvidia-gb200` on `a4x` (a4x-highgpu), 4 GPUs.
* - `rtx-pro-6000`: `nvidia-rtx-pro-6000` on `g4`; g4-standard-48 to -384 carry 1, 2, 4 or 8
*   GPUs. The fractional g4 shapes (1/8 to 1/2 GPU) are not in the table: a GPU count is an
*   integer from 1, and `research/gpu-accelerators.md` item 31 did not read how GCP reports them.
* - `gb300`: `nvidia-gb300` on `a4x` (a4x-maxgpu-4g-metal), 4 GPUs. Bare metal, so never spot
*   (`NEVER_SPOT_MACHINE_FAMILIES`).
*
* AWS entries (the AWS accelerated instance type tables; every family is x86_64):
*
* - `l4`: `nvidia-l4` on `g6`; g6 sizes carry 1 (xlarge to 8xlarge, 16xlarge), 4 (12xlarge,
*   24xlarge) or 8 (48xlarge) GPUs.
* - `l40s`: `nvidia-l40s` on `g6e`, with the same sizes: 1, 4 or 8 GPUs.
* - `h100`: `nvidia-h100-80gb` on `p5`; `p5.4xlarge` carries 1 GPU and `p5.48xlarge` 8.
* - `h200`: `nvidia-h200-141gb` on `p5e` and `p5en` (48xlarge), 8 GPUs.
* - `b200`: `nvidia-b200` on `p6-b200` (48xlarge), 8 GPUs.
* - `gb200`, `gb300`, `rtx-pro-6000`: no AWS entry.
*/
const ACCELERATOR_TABLE = Object.freeze({
	l4: Object.freeze({
		gcp: entry([["nvidia-l4", [
			1,
			2,
			4,
			8
		]]], ["g2"]),
		aws: entry([["nvidia-l4", [
			1,
			4,
			8
		]]], ["g6"])
	}),
	l40s: Object.freeze({ aws: entry([["nvidia-l40s", [
		1,
		4,
		8
	]]], ["g6e"]) }),
	h100: Object.freeze({
		gcp: entry([["nvidia-h100-80gb", [
			1,
			2,
			4,
			8
		]], ["nvidia-h100-mega-80gb", [8]]], ["a3"]),
		aws: entry([["nvidia-h100-80gb", [1, 8]]], ["p5"])
	}),
	h200: Object.freeze({
		gcp: entry([["nvidia-h200-141gb", [8]]], ["a3"]),
		aws: entry([["nvidia-h200-141gb", [8]]], ["p5e", "p5en"])
	}),
	b200: Object.freeze({
		gcp: entry([["nvidia-b200", [8]]], ["a4"]),
		aws: entry([["nvidia-b200", [8]]], ["p6-b200"])
	}),
	gb200: Object.freeze({ gcp: entry([["nvidia-gb200", [4]]], ["a4x"]) }),
	"rtx-pro-6000": Object.freeze({ gcp: entry([["nvidia-rtx-pro-6000", [
		1,
		2,
		4,
		8
	]]], ["g4"]) }),
	gb300: Object.freeze({ gcp: entry([["nvidia-gb300", [4]]], ["a4x"]) })
});
/**
* The entry for `model` on `cloud`, or `null`.
*
* Inputs: any values; both usually come from an app manifest that this function has not trusted.
* Output: the frozen entry when `model` is a member of {@link ACCELERATOR_MODELS}, `cloud` is
* `gcp` or `aws`, and the table holds an entry for the pair. `null` in every other case: an
* unknown model, an unknown cloud, a model the cloud does not sell (`l40s` on `gcp`, `gb200` on
* `aws`), and a prototype name such as `'constructor'` in either position. Never throws. Pure.
*/
function acceleratorEntry(model, cloud) {
	if (!isAcceleratorModel(model) || cloud !== "gcp" && cloud !== "aws") return null;
	const row = ACCELERATOR_TABLE[model];
	return Object.prototype.hasOwnProperty.call(row, cloud) ? row[cloud] : null;
}
/**
* The accelerator types of `model` on `cloud` whose shapes carry exactly `gpuCount` GPUs per node,
* in table order.
*
* Who calls it: the operator path that pins a GPU tier to catalog entries (S4, WS-4b). A catalog
* entry matches a tier when its `gpu.acceleratorType` is in this list and its GPU count equals
* `gpuCount`.
*
* Inputs: any values. Output: `['nvidia-h100-80gb', 'nvidia-h100-mega-80gb']` for `h100`, `gcp`
* and 8; `['nvidia-h100-80gb']` for `h100`, `gcp` and 1, and for `h100`, `aws` and 1 or 8. An
* empty list for `h100`, `aws` and 2, and when
* {@link acceleratorEntry} gives `null`, and when no shape sells `gpuCount`, a non-number
* included. Never throws. Pure.
*/
function acceleratorTypesFor(model, cloud, gpuCount) {
	const found = acceleratorEntry(model, cloud);
	if (found === null) return [];
	return found.shapes.filter((s) => s.gpuCounts.includes(gpuCount)).map((s) => s.acceleratorType);
}
/**
* The models the table sells on `cloud`, in {@link ACCELERATOR_MODELS} order. `gcp` gives
* `l4, h100, h200, b200, gb200, rtx-pro-6000, gb300`, and `aws` gives `l4, l40s, h100, h200, b200`.
* A value that is not `gcp` or `aws` gives an empty list. The publish rule
* `pool-tier-accelerator-unavailable` names this list in its message. Pure.
*/
function acceleratorModelsOn(cloud) {
	return ACCELERATOR_MODELS.filter((model) => acceleratorEntry(model, cloud) !== null);
}
//#endregion
export { ACCELERATOR_MODELS, ACCELERATOR_TABLE, acceleratorEntry, acceleratorModelSchema, acceleratorModelsOn, acceleratorTypesFor, isAcceleratorModel };

//# sourceMappingURL=accelerator-table.js.map